import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { ordersApi } from "../services/orders";
import { setlistsApi } from "../services/setlists";
import { usersApi } from "../services/users";
import type { MasspayHold, MinCashoutInfo, Order } from "../types/orders";
import { AuthContext } from "../context/AuthContext";
import OfferCard from "../components/OfferCard";
import DeclineModal from "../components/DeclineModal";
import "../components/FansLeaderboardSheet.css";
import "./RequestsPage.css";
import { User } from "../types/User";
import { groupOrdersByTrack } from "../utils/groupOrdersByTrack";
import { hasSeenDesktopPrompt, markDesktopPromptSeen } from "../utils/desktopPromptSeen";
import DesktopCompanionModal from "../components/DesktopCompanionModal";
import PendingEarningsBanner from "../components/PendingEarningsBanner";
import ClaimAlertBanner from "../components/ClaimAlertBanner";
import DesktopInstallBanner from "../components/DesktopInstallBanner";
import AppStoreDownloadPrompt from "../components/AppStoreDownloadPrompt";
import VerifyInstagramBanner from "../components/VerifyInstagramBanner";
import { useVerifyInstagramNudge } from "../hooks/useVerifyInstagramNudge";
import { useDesktopPing } from "../hooks/useDesktopPing";
import DemoCelebration from "../components/DemoCelebration";
import RecentRequestsBubble from "../components/RecentRequestsBubble";
import MonthlyEarningsCard from "../components/MonthlyEarningsCard";
import RequestHistoryModal from "../components/RequestHistoryModal";
import SwipeHint, { PlayHint } from "../components/SwipeHint";
import { takesMoney } from "../utils/takesMoney";
import { openMoneyGate } from "../lib/connectBank";
import "./RequestsHeadings.css";
import { DEMO_SONGS, demoHistoryItems, type DemoWhich } from "../lib/demoRequest";
import { useDemoRequests } from "../hooks/useDemoRequests";
import CardMoves from "../components/CardMoves";
import { endHint, hintDue, startPriceHint } from "../lib/welcomeHints";
import { holdForWelcome } from "../lib/firstSignInMoment";
import { askForAppRatingOnFirstPaidPlay } from "../lib/rateApp";
import RequestsHeader from "../components/RequestsHeader";
import WelcomeWave from "../components/WelcomeWave";
import ListeningBars from "../components/ListeningBars";
import FansModal from "../components/FansModal";
import WithdrawCounterModal from "../components/WithdrawCounterModal";
import PlaylistAcceptModal from "../components/PlaylistAcceptModal";
import CashoutFlow from "../components/CashoutFlow";
import { useFans } from "../hooks/useFans";
import { absUrl, num, type EarningsTotals } from "../utils/requestsFormat";
import { formatAmountForCopy } from "../utils/currency";
import { roundPriceStep } from "../utils/roundPriceStep";
import { cashoutDataFrom } from "../utils/cashoutData";
import { getCurrentAcceptingStatus, subscribeAcceptingStatus } from "../lib/acceptingStatus";
import { useAccountCache } from "../hooks/useAccountCache";
import { t } from "../i18n";

const RequestsPage: React.FC = () => {
  const { user, isAuthenticated } = useContext(AuthContext);
  // The lists and the profile paint from the last visit (hooks/useAccountCache)
  // and refresh quietly. A tap on a card acts only once this visit's first
  // read is in (stillListed below) — DISPLAY ONLY until then.
  const [pendingOffers, setPendingOffers] = useAccountCache<Order[]>("reqs-pending", []);
  const [acceptedOffers, setAcceptedOffers] = useAccountCache<Order[]>("reqs-accepted", []);
  const [counteredOffers, setCounteredOffers] = useAccountCache<Order[]>("reqs-countered", []);
  const listsFresh = useRef(false);
  // Withdraw-counter confirm popup. Holds the countered order
  // the swipe/button targeted; null = closed.
  const [withdrawTarget, setWithdrawTarget] = useState<Order | null>(null);
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  const { visits, visitsLoading, visitRange, setVisitRange, fans, fansMeta, showFansModal, setShowFansModal, openFans } = useFans();
  const [profile, setProfile] = useAccountCache<User | null>("reqs-profile", null);
  // This visit's server copy — the only one money paths (the cash-out, the
  // money gate, a counter's caps) read. null until it lands.
  const [freshProfile, setFreshProfile] = useState<User | null>(null);
  const [price, setPrice] = useState<number>(() => profile?.pricePerRequest ?? user?.pricePerRequest ?? 10);
  const applyProfile = useCallback(
    (me: User) => {
      setProfile(me);
      setFreshProfile(me);
      setPrice(me.pricePerRequest ?? 10);
    },
    [setProfile]
  );
  // Recent-requests bubble at the foot of the page. historyRefresh bumps
  // whenever a request is played/declined here, so the bubble picks the new
  // row up without a page reload.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [earningsByCur, setEarningsByCur] = useState<EarningsTotals>({});
  // The talent's resolved commission rate from /earnings (0.15 standard, or an
  // admin override). null until loaded / on an older server → modal uses 15%.
  const [commissionRate, setCommissionRate] = useState<number | null>(null);
  // Live / Offline, from the shared status every surface broadcasts to — it
  // decides whether the "listening for requests" dial turns.
  const [isAccepting, setIsAccepting] = useState(getCurrentAcceptingStatus() !== false);
  useEffect(() => subscribeAcceptingStatus(setIsAccepting), []);
  // DJ voluntary refunds: card fees owed from refunding played requests,
  // per currency, straight from /orders/earnings. Drives the cashout modal's
  // "Refund card fees (N)" line; the server does the actual deduction at
  // /payments/cashout. Empty for talents who never refunded (older server →
  // field absent → empty).
  const [refundFeesByCur, setRefundFeesByCur] = useState<
    Record<
      string,
      {
        amount: number;
        count: number;
        // Dispute-loss ledger: the same ledger carries a lost
        // dispute's share / Stripe's fee. Absent kind = a refund card fee.
        disputeCount?: number;
        items: { songTitle: string; amount: number; refundedAt?: string | null; kind?: "refund_fee" | "dispute_share" | "dispute_fee" }[];
      }
    >
  >({});
  // Minimum cashout: the transfer greys out under the currency's $5-class
  // threshold unless the oldest uncashed request is ≥ waiverDays old. Live
  // values ride on /orders/earnings; the payinConfig mirror is the fallback
  // for an older server (CashoutFlow does the math).
  const [minCashoutInfo, setMinCashoutInfo] = useState<MinCashoutInfo>({
    byCurrency: {},
    waiverDays: 80,
    oldestUnsettledAt: null,
  });
  // MassPay talent: the part of the balance still inside the fraud hold.
  const [masspayHold, setMasspayHold] = useState<MasspayHold | null>(null);
  // Setlist sales awaiting the track-history upload (accepted, uncaptured).
  // Drives the cashout modal's "not included" warning line; the Balance tab
  // renders the amber upload card off the same endpoint.
  const [setlistPending, setSetlistPending] = useState<{ count: number; total: number; currency: string | null }>({
    count: 0,
    total: 0,
    currency: null,
  });
  // Playlist accept confirm (founder copy, v4 DJ 05) — the swiped sale waits
  // here until the DJ confirms; null = closed.
  const [playlistAccept, setPlaylistAccept] = useState<{ ids: string[]; amount: number; currency?: string } | null>(null);
  const [playlistAcceptBusy, setPlaylistAcceptBusy] = useState(false);
  // Desktop Companion pop-up ("Never Miss a Request"). It
  // fires once the DJ has finished the DEMO practice cards (1.5.1: after
  // "You got it!", not between the two cards) — not on first mount (founder:
  // the tour is the one first-sign-in container, and never after a REAL
  // request — that DJ is live and busy).
  // Once per account per device (localStorage); never inside the Electron
  // companion, where it wouldn't burn the flag either.
  const [showDesktopPrompt, setShowDesktopPrompt] = useState(false);
  const queueDesktopPrompt = () => {
    if (!user) return;
    if ((window as any).electronAPI?.isElectron) return;
    if (hasSeenDesktopPrompt(user.slug)) return;
    markDesktopPromptSeen(user.slug);
    setShowDesktopPrompt(true);
  };

  // The one-time price bubble under the Request Price box (founder pick A,
  // build 44): due from the end of the practice cards on this device until
  // the first tap on the box (lib/welcomeHints). It waits behind "You got it!"
  // and the Desktop Companion pop-up.
  const [priceCoach, setPriceCoach] = useState(false);
  useEffect(() => setPriceCoach(hintDue("price", user?.slug)), [user?.slug]);
  const endCoach = () => {
    if (user) endHint("price", user.slug);
    setPriceCoach(false);
  };

  // DEMO practice requests (new-DJ onboarding; two cards, one at a time —
  // founder, 1.5.1 #6). Loaded from the profile fetch below, advanced
  // optimistically, synced to the account fire-and-forget (useDemoRequests).
  // Finishing them is also the Desktop Companion pop-up's moment (once per
  // device): it waits behind "You got it!", never between the two cards —
  // and the price bubble's. Never an Order, never money.
  const demo = useDemoRequests(user?.slug, () => {
    queueDesktopPrompt();
    if (!user) return;
    startPriceHint(user.slug);
    setPriceCoach(true);
  });
  // A practice decline goes through the real reasons sheet, marked practice
  // (founder, build 44): nothing is sent, and any answer finishes the card.
  const [practiceDecline, setPracticeDecline] = useState<DemoWhich | null>(null);
  // The practice comes first (founder, 2026-10-11): while it's on — the cards
  // (or the profile they come from, still loading), "You got it!" and the
  // Desktop pop-up right after it — Apple's notification question waits, and
  // the Wallet step behind it (lib/firstSignInMoment; up to 10 minutes).
  // Face ID keeps its place at the start.
  const practiceOn =
    demo.cards === null || !!demo.pending || demo.queued.length > 0 || demo.celebrating || showDesktopPrompt;
  useEffect(() => holdForWelcome(practiceOn), [practiceOn]);
  useEffect(() => () => holdForWelcome(false), []);
  useVerifyInstagramNudge(user, freshProfile, practiceOn);
  const loadDemos = demo.load;

  useEffect(() => {
    const fetchProfile = async () => {
      if (!isAuthenticated || !user) return;
      try {
        const me = await usersApi.get(user.slug);
        applyProfile(me);
        // DEMO cards resume exactly where the account left off: a mid-flow
        // card (accepted but never cleared) comes back in Queued; a handled
        // card never comes back (the server computes the offer flags).
        loadDemos(me);
      } catch (err) {
        console.error("Ошибка при загрузке профиля:", err);
      }
    };
    fetchProfile();
  }, [isAuthenticated, user, loadDemos, applyProfile]);

  // The header's money taps (the price pad, the money gate) wait for this
  // visit's profile when the page painted from the last one.
  const refreshProfile = useCallback(async (): Promise<User | null> => {
    if (!user) return null;
    try {
      const me = await usersApi.get(user.slug);
      applyProfile(me);
      return me;
    } catch {
      return null;
    }
  }, [user, applyProfile]);

  // Live preference apply: profile is fetched once on
  // mount, but since the Profile panel became a push-drawer it opens
  // OVER this tab without unmounting it — a preference saved there (card
  // size, price, name…) stayed stale here until a full app restart.
  // ProfilePage broadcasts "profile:patched" after every successful save;
  // re-pull the profile so every preference applies live. Deliberately only
  // profile + price — the first-sign-in popups above are mount-only.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const onProfilePatched = async () => {
      try {
        const me = await usersApi.get(user.slug);
        applyProfile(me);
      } catch {
        /* stale until the next mount — same as before this listener */
      }
    };
    window.addEventListener("profile:patched", onProfilePatched);
    return () => window.removeEventListener("profile:patched", onProfilePatched);
  }, [user, isAuthenticated, applyProfile]);

  type Lists = { pending: Order[]; accepted: Order[]; countered: Order[] };
  const fetchAll = async (): Promise<Lists | null> => {
    try {
      const [pending, accepted, earnings, countered, setlistRes] = await Promise.all([
        ordersApi.pending(),
        ordersApi.accepted(),
        ordersApi.earnings(),
        // Awaiting-counter orders from the last 48h. An older server has no
        // route yet (404) — treat any failure as an empty list, silently.
        ordersApi.countered().catch(() => [] as Order[]),
        // Setlist sales waiting on the track-history upload. Failure (or an
        // older server) → zeros: the warning line just stays hidden.
        setlistsApi.pending().catch(() => ({ count: 0, total: 0, currency: null })),
      ]);

      setPendingOffers(pending || []);
      setAcceptedOffers(accepted || []);
      setCounteredOffers(countered || []);
      listsFresh.current = true;
      setSetlistPending({
        count: num(setlistRes?.count),
        total: num(setlistRes?.total),
        currency: setlistRes?.currency || null,
      });

      // The Transfer-to-Bank data (utils/cashoutData, shared with Balance).
      const cash = cashoutDataFrom(earnings, profile?.currency);
      setEarningsByCur(cash.earningsByCur);
      setCommissionRate(cash.commissionRate);
      setRefundFeesByCur(cash.refundFeesByCur);
      setMinCashoutInfo(cash.minCashoutInfo);
      setMasspayHold(cash.masspayHold);
      return { pending: pending || [], accepted: accepted || [], countered: countered || [] };
    } catch (err) {
      // Keep what the talent last saw (2026-10-10): one failed refresh (venue
      // Wi-Fi, a deploy blip) used to empty the queue and the earnings — and
      // the Play hint read that as "nothing real queued" — until a later poll
      // came back. The 10 s poll below simply tries again.
      console.error("Fetch error:", err);
      return null;
    }
  };

  // Cards painted from the last visit are on screen before this visit's first
  // read lands. A tap in that moment acts only on what the server still lists
  // there — a request handled since on another device (the desktop app, a
  // second phone) is never accepted, played, declined or withdrawn twice.
  const stillListed = async (ids: string[], from: (keyof Lists)[]): Promise<string[]> => {
    if (listsFresh.current) return ids;
    const fresh = await fetchAll();
    if (!fresh) return [];
    const live = new Set(from.flatMap((k) => fresh[k].map((o) => o._id)));
    return ids.filter((id) => live.has(id));
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchAll();
    const interval = setInterval(fetchAll, 10000);
    return () => clearInterval(interval);
    // fetchAll is recreated per render; polling is keyed on sign-in only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useDesktopPing(isAuthenticated);

  // Resolves true once the server took the action.
  const handleStackAction = async (
    listedIds: string[],
    action: "accept" | "play"
  ): Promise<boolean> => {
    const stackedIds = await stillListed(listedIds, [action === "play" ? "accepted" : "pending"]);
    if (!stackedIds.length) return false;
    try {
      await ordersApi.batch(action, stackedIds);
      // Played is final: the cards leave Queued now, even when the refresh
      // below fails and keeps the old lists (2026-10-10) — a stale card's
      // Play would run the capture again and re-send the fan's receipt.
      if (action === "play") setAcceptedOffers((prev) => prev.filter((o) => !stackedIds.includes(o._id)));
      await fetchAll();
      // Only "play" writes a history row; "accept" just moves the card to
      // Queued, so the bubble has nothing new to show.
      if (action === "play") setHistoryRefresh((k) => k + 1);
      return true;
    } catch (err: any) {
      const serverMsg =
        err?.response?.data?.msg || err?.message || t("requestsPage.unknownError");
      console.error(`Batch ${action} failed:`, serverMsg);
      return false;
    }
  };

  // Orders whose Play is still on its way. A second tap during the first
  // sent a second capture: Stripe refused it (same idempotency key), the
  // server flagged a capture failure that stuck although the money moved
  // once, and the play left History for Payment Issues (2026-10-10). One
  // Play per order at a time — the ref answers even before a re-render; the
  // state disables the button meanwhile.
  const playingRef = useRef(new Set<string>());
  const [playingIds, setPlayingIds] = useState<string[]>([]);

  // Play a queued stack. A REAL paid play is the app-rating moment (founder,
  // 1.5.1 #4 — once per install, lib/rateApp); a free request ($0) never
  // asks, and the practice cards don't come through here at all.
  const playStack = async (stack: { stackedIds: string[]; totalStackAmount: number }) => {
    const ids = stack.stackedIds;
    if (ids.some((id) => playingRef.current.has(id))) return;
    ids.forEach((id) => playingRef.current.add(id));
    setPlayingIds(Array.from(playingRef.current));
    try {
      const played = await handleStackAction(ids, "play");
      if (played && stack.totalStackAmount > 0) askForAppRatingOnFirstPaidPlay();
    } finally {
      ids.forEach((id) => playingRef.current.delete(id));
      setPlayingIds(Array.from(playingRef.current));
    }
  };

  const [declineStackIds, setDeclineStackIds] = useState<string[] | null>(null);

  // Playlist sales confirm before accepting (founder copy: "Upload your track
  // history .CSV within 2 days → fans receive your playlist → you get paid.").
  // Song requests keep the direct swipe-accept.
  const requestAccept = (stack: { stackedIds: string[]; totalStackAmount: number; lead: Order }) => {
    if (stack.lead.isPlaylist) {
      setPlaylistAccept({
        ids: stack.stackedIds,
        amount: stack.totalStackAmount,
        currency: stack.lead.currency || profile?.currency,
      });
      return;
    }
    handleStackAction(stack.stackedIds, "accept");
  };

  const confirmPlaylistAccept = async () => {
    if (!playlistAccept || playlistAcceptBusy) return;
    setPlaylistAcceptBusy(true);
    try {
      await handleStackAction(playlistAccept.ids, "accept");
    } finally {
      setPlaylistAcceptBusy(false);
      setPlaylistAccept(null);
    }
  };

  // Push-notification quick-accept only carries an orderId — look the order
  // up so a playlist sale still gets its confirm popup instead of an instant
  // accept. Unknown id (already handled elsewhere) → the normal accept call,
  // which the server no-ops safely.
  const acceptByIdWithPlaylistGuard = async (orderId: string) => {
    try {
      const data = await ordersApi.pending();
      const hit = (data || []).find((o) => o._id === orderId);
      if (hit?.isPlaylist) {
        setPlaylistAccept({
          ids: [orderId],
          amount: num(hit.amount),
          currency: hit.currency || profile?.currency,
        });
        return;
      }
    } catch {
      /* lookup is best-effort — fall through to the normal accept */
    }
    await handleStackAction([orderId], "accept");
  };

  // Handle deep-links from push notification clicks: ?focus=<orderId>&action=accept|decline
  // (fires once on mount if URL has focus/action params)
  const processedNotifRef = useRef<string | null>(null);
  // A card that changes section glides there instead of jumping (founder,
  // build 44 — components/CardMoves watches the cards in this scroller).
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const focus = params.get("focus");
    const action = params.get("action");
    if (!focus) return;

    const key = `${focus}:${action || "view"}`;
    if (processedNotifRef.current === key) return;
    processedNotifRef.current = key;

    (async () => {
      if (action === "accept") {
        await acceptByIdWithPlaylistGuard(focus);
      } else if (action === "decline") {
        setDeclineStackIds([focus]);
      }
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    })();
    // Once per deep-link arrival (processedNotifRef guards repeats); the
    // accept helper is recreated per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Listen for service-worker messages from notification clicks when the PWA
  // is already open. Registered unconditionally so it catches clicks in any state.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!("serviceWorker" in navigator)) return;

    const onSwMessage = (event: MessageEvent) => {
      if (event.data?.type === "NOTIFICATION_CLICK") {
        const { orderId, action: swAction } = event.data;
        if (!orderId) return;
        if (swAction === "accept") acceptByIdWithPlaylistGuard(orderId);
        else if (swAction === "decline") setDeclineStackIds([orderId]);
      }
    };
    navigator.serviceWorker.addEventListener("message", onSwMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onSwMessage);
    // One listener per sign-in; the accept helper is recreated per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // DJ withdraws a counter the fan is ghosting. Pure state
  // change server-side (a live counter carries no money — every hold was
  // released at decline time). A 400 means the fan just paid or passed in the
  // same instant; either way the refetch below converges the lists.
  const confirmWithdraw = async () => {
    if (!withdrawTarget || withdrawBusy) return;
    setWithdrawBusy(true);
    try {
      if ((await stillListed([withdrawTarget._id], ["countered"])).length)
        await ordersApi.withdrawCounter(withdrawTarget._id);
    } catch (err: any) {
      console.error("withdraw-counter error:", err?.response?.data || err?.message);
    } finally {
      setWithdrawBusy(false);
      setWithdrawTarget(null);
      await fetchAll();
      setHistoryRefresh((k) => k + 1);
    }
  };

  const closeDeclineModal = () => setDeclineStackIds(null);
  const confirmDecline = async (
    reason: "pas_ambience" | "no_access" | "already_played" | "more_money" | "not_sharing_setlist",
    counterAmount?: number
  ) => {
    if (!declineStackIds?.length) return;
    try {
      const declined = await stillListed(declineStackIds, ["pending", "accepted"]);
      if (!declined.length) return closeDeclineModal();
      await ordersApi.batchDecline({ orderIds: declined, reason, counterAmount });
      closeDeclineModal();
      // Same for a decline: those cards go now, whatever the refresh does
      // (a second decline would refund and email the fan again).
      setPendingOffers((prev) => prev.filter((o) => !declined.includes(o._id)));
      setAcceptedOffers((prev) => prev.filter((o) => !declined.includes(o._id)));
      await fetchAll();
      setHistoryRefresh((k) => k + 1);
    } catch (err: any) {
      // The server refuses a counter from talent who can't take money (1.5.2
      // b49) — this page still thought they could: their next step instead.
      if (err?.response?.data?.code === "bank_required" && profile) {
        closeDeclineModal();
        openMoneyGate(profile);
        fetchAll();
        return;
      }
      console.error("Decline error:", err?.response?.data || err?.message);
    }
  };

  // Приводим любые относительные пути к абсолютным
  const getAvatarUrl = (url?: string) => absUrl(url);

  // A practice card is the real OfferCard with its song, the DJ's own price
  // (on the green panel only) and the DEMO chip.
  const pendingDemo = demo.pending;
  const demoCardProps = (which: DemoWhich) => ({
    _id: DEMO_SONGS[which].id,
    searchQuery: DEMO_SONGS[which].searchQuery,
    trackId: DEMO_SONGS[which].trackId,
    avatarUrl: DEMO_SONGS[which].trackImage,
    trackArtists: [...DEMO_SONGS[which].trackArtists],
    amount: num(price),
    currency: profile?.currency,
    isDemo: true,
  });
  // Handled practice cards as rows in the bubble and the full history.
  const demoItems = demoHistoryItems(demo.cards, profile?.pricePerRequest, profile?.currency);
  // Nothing left to swipe and only practice in Queued: point at its green
  // Play button (founder, 1.5.1 #6 — "people might just leave it there").
  // Never while a card waits in Pending (its own hint leads) or a real
  // request is queued (a paying fan's button is not practice).
  const playNudge =
    demo.queued.length > 0 && !pendingDemo && pendingOffers.length === 0 && !acceptedOffers.some((o) => !o.isPlaylist);
  // Queued only exists once something is queued (playlist sales never queue).
  const queuedVisible = acceptedOffers.some((o) => !o.isPlaylist) || demo.queued.length > 0;
  // "Pending Requests · 0" / "Queued · 2" (founder, 1.5.2 b48): the cards each
  // section shows, the practice cards included.
  const pendingCount = pendingOffers.length + demo.pendings.length;
  const queuedCount = acceptedOffers.filter((o) => !o.isPlaylist).length + demo.queued.length;
  // The hint names the button's own label (OfferCard playLabel): "$10", "€5".
  const demoPlayLabel = formatAmountForCopy(roundPriceStep(num(price), profile?.currency), profile?.currency);

  return (
    <>
      {/* The page owns its scroll (a P0 fix). iOS rubber-band drags
          position:fixed elements whenever the DOCUMENT scrolls — that is what
          dragged the green header over the status bar and lifted the tab bar
          off the bottom (founder, native 1.1(34)). With a fixed scroller
          between the header and the screen bottom the document never scrolls,
          so there is nothing to rubber-band. Twin of the fan tabs'
          .fanreq-scroll / .fanlb-scroll / .fanhome-scroll. */}
      <div className="reqs-scroll" ref={scrollRef}>
      <CardMoves root={scrollRef} />
    <div className={`requests-container cards--${profile?.requestCardSize || "compact"}`}>
      {/* Everything above the activity bubble. min-height (see .requests-fold)
          keeps Queued + Pending owning the screen: with few requests the
          bubble starts ~4/5 down — two rows peek above the tab bar, the rest
          scrolls (founder call, 2026-08-07). With many requests the fold is
          taller than its minimum anyway and flow is unchanged. */}
      <div className="requests-fold">
      {/* "Wasn't played?" claims waiting on this talent — above everything,
          outside every card (founder, 2026-10-09). */}
      <ClaimAlertBanner />
      <PendingEarningsBanner />
      <DesktopInstallBanner />
      {/* iPhone web DJs, once, only when the App Store listing is live. */}
      <AppStoreDownloadPrompt />

      {/* "Welcome" over the photo + name: the dashboard says hello (founder, build 44). */}
      <WelcomeWave />
      <RequestsHeader
        profile={profile}
        account={user}
        freshProfile={freshProfile}
        onProfile={applyProfile}
        refreshProfile={refreshProfile}
        price={price}
        setPrice={setPrice}
        visits={visits}
        visitsLoading={visitsLoading}
        onOpenFans={openFans}
        coach={priceCoach && !demo.celebrating && !showDesktopPrompt}
        onCoachDone={endCoach}
      />
      <VerifyInstagramBanner profile={freshProfile ?? profile} />

      {/* Queued only exists once something is queued (founder decision) — an
          empty "Queued" heading over nothing was noise. */}
      {queuedVisible && (
      <div className="queued">
        <h2>
          {t("requestsPage.queued")}
          <span className="sec-count"> · {queuedCount}</span>
        </h2>
        {playNudge && <PlayHint amount={demoPlayLabel} />}
        {/* Accepted playlist sales never queue here: the Play button captures
            money, and a playlist sale only captures at DELIVERY (the upload).
            Their tracker is the amber card on the Balance tab. */}
        {groupOrdersByTrack(acceptedOffers.filter((o) => !o.isPlaylist)).map((stack) => (
          <OfferCard
            key={stack.lead._id}
            _id={stack.lead._id}
            fanBoost={stack.anyFanBoost}
            searchQuery={stack.lead.searchQuery}
            trackId={stack.lead.trackId}
            amount={stack.totalStackAmount}
            avatarUrl={getAvatarUrl(stack.lead.trackImage || "")}
            trackArtists={Array.isArray(stack.lead.trackArtists) ? stack.lead.trackArtists : []}
            status="accepted"
            comment={stack.comments[0]}
            onPlay={() => playStack(stack)}
            moveKey={stack.lead._id}
            moveList="queued"
            playBusy={stack.stackedIds.some((id) => playingIds.includes(id))}
            onDecline={() => setDeclineStackIds(stack.stackedIds)}
            isPriority={stack.highestTier === "fast"}
            tier={stack.highestTier}
            currency={stack.lead.currency || profile?.currency}
            createdAt={stack.lead.createdAt}
            clientNickname={stack.lead.clientNickname}
            clientInstagram={stack.lead.clientInstagram}
            clientInstagramVerified={stack.lead.clientInstagramVerified}
            stackCount={stack.stackCount}
            contributors={stack.contributors}
            originalAmount={stack.lead.originalAmount}
            increaseCount={stack.lead.increaseCount}
            totalPaid={stack.lead.totalAmount}
          />
        ))}
        {/* DEMO practice cards ride below any real queued cards — a paying
            fan always outranks practice. */}
        {demo.queued.map((which) => (
          <OfferCard
            key={DEMO_SONGS[which].id}
            {...demoCardProps(which)}
            status="accepted"
            moveKey={DEMO_SONGS[which].id}
            moveList="queued"
            nudgePlay={playNudge && which === demo.queued[0]}
            onPlay={() => demo.play(which)}
            onDecline={() => setPracticeDecline(which)}
          />
        ))}
      </div>
      )}

      <div className="pending">
        {/* The listening bars beside the heading (build 44, replacing the
            orbit spinner) ONLY run while the section is empty — a "listening
            for requests" signal, so the moment a card exists they disappear.
            Below the heading it swaps the same way: the drawn empty state
            while nothing waits, the gesture line once there's something to
            swipe. */}
        <div className="pending-head">
          <h2>
            {t("requestsPage.pendingRequests")}
            <span className="sec-count"> · {pendingCount}</span>
          </h2>
          {!(pendingOffers.length > 0 || pendingDemo) && (
            // Offline = not listening: the bars stop and grey out (founder, build 39 / 44).
            <ListeningBars live={isAccepting} />
          )}
        </div>
        {/* The practice card's exact direction only while it's the one card
            waiting: a "decline" hint above a REAL paying request would
            point the talent the wrong way. */}
        {/* An empty page is just the faint R below, with the listening bars
            beside the heading (founder, 1.5.2 b49: the big bars went, after
            the drawing and its line in b48). */}
        {(pendingOffers.length > 0 || pendingDemo) && (
          <SwipeHint demoAsk={pendingOffers.length === 0 && pendingDemo ? pendingDemo.ask : null} />
        )}
        {/* Faint R (founder C6 + rider): ALWAYS there — z-index:-1
            keeps it behind everything, so incoming request cards simply
            overlap it. Its room is the fold's min-height (.requests-fold),
            no spacer of its own: a 40vh spacer under the drawn empty state
            pushed History below the tab bar on a quiet night (founder, 1.5.2
            b45: "you should be able to see one and a half"). With it gone,
            History sits at the same height empty, queued-only or with a
            couple of cards, so it never moves when the lists land. */}
        <img src="/r.png" alt="" className="pending-bg-r" aria-hidden="true" />
        {groupOrdersByTrack(pendingOffers).map((stack) => (
          <OfferCard
            key={stack.lead._id}
            _id={stack.lead._id}
            fanBoost={stack.anyFanBoost}
            searchQuery={stack.lead.searchQuery}
            amount={stack.totalStackAmount}
            trackId={stack.lead.trackId}
            avatarUrl={getAvatarUrl(stack.lead.trackImage || "")}
            trackArtists={Array.isArray(stack.lead.trackArtists) ? stack.lead.trackArtists : []}
            status="pending"
            comment={stack.comments[0]}
            isPlaylist={!!stack.lead.isPlaylist}
            trackImages={stack.lead.trackImages}
            onAccept={() => requestAccept(stack)}
            moveKey={stack.lead._id}
            moveList="pending"
            onDecline={() => setDeclineStackIds(stack.stackedIds)}
            isPriority={stack.highestTier === "fast"}
            tier={stack.highestTier}
            currency={stack.lead.currency || profile?.currency}
            createdAt={stack.lead.createdAt}
            clientNickname={stack.lead.clientNickname}
            clientInstagram={stack.lead.clientInstagram}
            clientInstagramVerified={stack.lead.clientInstagramVerified}
            stackCount={stack.stackCount}
            contributors={stack.contributors}
            originalAmount={stack.lead.originalAmount}
            increaseCount={stack.lead.increaseCount}
            totalPaid={stack.lead.totalAmount}
          />
        ))}
        {/* Both practice cards from the start (founder, 1.5.2 b49), card 1
            first; only the first one nudges, so the swipe it teaches is
            clear. Keyed per card. */}
        {demo.pendings.map((d, i) => (
          <OfferCard
            key={DEMO_SONGS[d.which].id}
            {...demoCardProps(d.which)}
            status="pending"
            moveKey={DEMO_SONGS[d.which].id}
            moveList="pending"
            nudge={i === 0 ? d.ask : undefined}
            declineOnly={d.which === 2}
            createdAt={new Date().toISOString()}
            onAccept={() => demo.accept(d.which)}
            onDecline={() => setPracticeDecline(d.which)}
          />
        ))}
        {/* Countered requests sit below the pending stack — awaiting the
            fan's response. Rendered individually (never grouped with real
            pending orders); when the fan pays, the server flips the order
            back to "pending" and it moves lists on the next poll. */}
        {counteredOffers.map((o) => (
          <OfferCard
            key={o._id}
            _id={o._id}
            moveKey={o._id}
            moveList="countered"
            searchQuery={o.searchQuery}
            amount={o.amount}
            trackId={o.trackId}
            avatarUrl={getAvatarUrl(o.trackImage || "")}
            trackArtists={Array.isArray(o.trackArtists) ? o.trackArtists : []}
            status="countered"
            counterAmount={o.counterAmount}
            isPlaylist={!!o.isPlaylist}
            trackImages={o.trackImages}
            comment={o.comment}
            currency={o.currency || profile?.currency}
            createdAt={o.createdAt}
            clientNickname={o.clientNickname}
            clientInstagram={o.clientInstagram}
            clientInstagramVerified={o.clientInstagramVerified}
            onWithdrawCounter={() => setWithdrawTarget(o)}
          />
        ))}
      </div>
      </div>

      {/* Recent activity — the page used to bottom out into black below the
          Pending stack. Three most recent handled requests + "See all".
          "History" header (founder 8/26): the bubble was the one section on
          the page floating label-less — same h2 treatment as Queued /
          Pending Requests, sitting right on top of it. */}
      <div className="history-sec">
        <h2>{t("requestsPage.history")}</h2>
      </div>
      <RecentRequestsBubble
        onSeeAll={() => setHistoryOpen(true)}
        refreshKey={historyRefresh}
        demoItems={demoItems}
      />

      {/* How the month is going, under what just happened. Same refresh
          counter as the bubble, so playing a request moves both. */}
      <MonthlyEarningsCard refreshKey={historyRefresh} />
      </div>
      </div>

      {/* ── Overlays ──────────────────────────────────────────────────────
          Every sheet / modal / popup on this page renders OUTSIDE the
          scroller. STACKING CONTRACT v2: a composited scroll container can
          clip its position:fixed descendants on iOS WebKit, which would trap
          these inside the scroll box. Out here their containing block is the
          viewport, exactly as before this page was contained. */}
      {/* Waits behind the demo celebration when both fire on the same tap
          (decline / play): one overlay at a time. */}
      {showDesktopPrompt && !demo.celebrating && (
        <DesktopCompanionModal onDismiss={() => setShowDesktopPrompt(false)} />
      )}

      <DeclineModal
        isOpen={!!declineStackIds || practiceDecline !== null}
        practice={practiceDecline !== null}
        currency={profile?.currency}
        capsLifted={freshProfile?.capsLifted}
        talent={freshProfile ?? profile}
        isPlaylist={
          !!declineStackIds &&
          [...pendingOffers, ...acceptedOffers, ...counteredOffers].some(
            (o) => declineStackIds.includes(o._id) && o.isPlaylist
          )
        }
        onClose={practiceDecline !== null ? () => setPracticeDecline(null) : closeDeclineModal}
        // No bank yet: asking for more money opens Connect Bank, practice
        // card included (founder, 1.5.2 b49).
        onNeedBank={
          profile && !takesMoney(profile)
            ? () => {
                setPracticeDecline(null);
                closeDeclineModal();
                openMoneyGate(profile);
              }
            : undefined
        }
        onConfirm={
          practiceDecline !== null
            ? () => {
                demo.decline(practiceDecline);
                setPracticeDecline(null);
              }
            : confirmDecline
        }
      />

      {/* Withdraw-counter confirm (founder copy). Backdrop
          uses the pointerdown guard — this opens from a tap low on the page,
          the exact spot iOS ghost-clicks used to instantly close overlays. */}
      {withdrawTarget && (
        <WithdrawCounterModal
          busy={withdrawBusy}
          onCancel={() => setWithdrawTarget(null)}
          onConfirm={confirmWithdraw}
        />
      )}

      {/* Playlist sale accept confirm (v4 DJ 05 — founder copy verbatim).
          Accept only moves the card; the fan is charged at DELIVERY. */}
      {playlistAccept && (
        <PlaylistAcceptModal
          sale={playlistAccept}
          busy={playlistAcceptBusy}
          onCancel={() => setPlaylistAccept(null)}
          onConfirm={confirmPlaylistAccept}
        />
      )}

      {historyOpen && (
        <RequestHistoryModal
          onClose={() => setHistoryOpen(false)}
          onMoneyChanged={() => {
            // A refund moved real money: refresh the balance/earnings numbers
            // and the recent-activity bubble so every surface agrees.
            setHistoryRefresh((x) => x + 1);
            fetchAll();
          }}
          demoItems={demoItems}
        />
      )}

      {/* "You got it!" — once, after the last practice card is handled. */}
      {demo.celebrating && <DemoCelebration onClose={demo.endCelebration} />}

      <CashoutFlow
        earningsByCur={earningsByCur}
        setEarningsByCur={setEarningsByCur}
        commissionRate={commissionRate}
        refundFeesByCur={refundFeesByCur}
        setlistPending={setlistPending}
        minCashoutInfo={minCashoutInfo}
        masspayHold={masspayHold}
        profile={freshProfile}
        fetchAll={async () => {
          await fetchAll();
        }}
      />

      {showFansModal && (
        <FansModal
          visits={visits}
          visitRange={visitRange}
          setVisitRange={setVisitRange}
          fans={fans}
          fansMeta={fansMeta}
          onClose={() => setShowFansModal(false)}
        />
      )}

    </>
  );
};

export default RequestsPage;
